import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { getPinIn, getUserInfo, postLogin } from '../api/portal/auth.api';
import { logConsole, logTransaction } from '../common/log';
import { handleThunkError } from '../common/util';
import { AttachmentAssetType, getEmptyAttachmentContainer } from '../models/dms/attachment.model';
import { ITechnician } from '../models/dms/technician.model';
import { LogType } from '../models/log.model';
import { IJwt, ITokenData } from '../models/portal/auth.model';
import { ConfigName, updateConfigBool, updateConfigObject, updateConfigString } from '../sqlite/common/config';
import { resetAppStateAsync, setIsLoadingCountState } from './appSlice';
import { setAttendanceClockingStoreAsync, setLabourClockingStoreAsync, setServerClockingDetailStoreAsync } from './technicianClockingSlice';
import { setAdhocCommentCacheStoreAsync, setSelectedTechnicianCommentsState } from './technicianCommentSlice';
import { setAdhocAttachmentCacheStoreAsync, setSelectedVehicleAttachmentsStoreAsync } from './vehicleAttachmentSlice';
import { setWorkCategoriesSyncStoreAsync } from './workCategorySyncSlice';
import { setFiltersOrSortsStoreAsync, setOrderByDateDescendingStoreAsync, setSelectedBranchCodeStoreAsync, setTechCodeCheckboxStoreAsync, setTodaysBookingsCheckboxStoreAsync } from './filterMenuSlice';
import { setGpsServicesUserEnabledStoreAsync } from './settingsSlice';

interface IAuthState {
  isAuthenticated: boolean;
  token: IJwt | undefined;
  technician: ITechnician | undefined;
  isAuthenticating: boolean;
  isPinningIn: boolean;
  isRefreshingToken: boolean;
  selectedDealerCode: string;
}

const initialState: IAuthState = {
  isAuthenticated: false,
  token: undefined,
  technician: undefined,
  isAuthenticating: false,
  isPinningIn: false,
  isRefreshingToken: false,
  selectedDealerCode: '',
};

/// THUNKS

export const setIsAuthenticatedStoreAsync = createAsyncThunk<{}, boolean>('auth/setIsAuthenticatedStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting is authenticated to '${val}'`, '[auth] setIsAuthenticatedStoreAsync');
  try {
    dispatch(setIsAuthenticatedState(val));
    await updateConfigBool(ConfigName.IsAuthenticated, val, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setTokenStoreAsync = createAsyncThunk<{}, IJwt>('auth/setTokenStoreAsync', async (token, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting token for '${token.userName}'`, '[auth] setTokenStoreAsync');
  try {
    dispatch(setTokenState(token));
    await updateConfigObject<IJwt>(ConfigName.AuthToken, token, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setTechnicianStoreAsync = createAsyncThunk<{}, ITechnician>('auth/setTechnicianStoreAsync', async (tech, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting technician to '${tech.code}' (${tech.displayName})`, '[auth] setTechnicianStoreAsync');
  try {
    dispatch(setTechnicianState(tech));
    await updateConfigObject<ITechnician>(ConfigName.Technician, tech, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const signOutAsync = createAsyncThunk<{}, void>('auth/signOutAsync', async (e, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Signing user out`, '[auth] signOutAsync');
  try {
    dispatch(setTokenState(undefined));
    dispatch(setIsAuthenticatedState(false));

    await dispatch(pinOutAsync());

    // Reset app state
    await dispatch(resetAppStateAsync({ clearAll: true }));

    await updateConfigObject<IJwt>(ConfigName.AuthToken, {} as IJwt, false);
    await updateConfigBool(ConfigName.IsAuthenticated, false, false);
    await updateConfigObject<ITechnician>(ConfigName.Technician, {} as ITechnician, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const signInAsync = createAsyncThunk<{}, { username: string; password: string; databaseId: number; otp?: string; clearUserData?: boolean }>('auth/signInAsync', async (credentials, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Signing user in for user '${credentials.username}' and database '${credentials.databaseId}'`, '[auth] signInAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setIsAuthenticatingState(true));

    const token = await postLogin(credentials.username, credentials.password, credentials.databaseId.toString(), credentials.otp);

    if (token.access_token) {
      const jwt = JSON.parse(atob(token.access_token.split('.')[1])) as ITokenData;

      let newToken = {
        ...token,
        displayUsername: jwt.userid,
        databaseId: jwt.dbid,
        moduleCode: jwt.moduleCode,
        hasOtp: credentials.otp !== undefined,
      } as IJwt;

      await dispatch(setTokenStoreAsync(newToken));

      const userInfo = await getUserInfo(newToken.displayUsername);

      if (userInfo.contact_code) {
        await dispatch(
          setTokenStoreAsync({
            ...newToken,
            contactCode: userInfo.contact_code,
          }),
        );
      }
      await dispatch(setIsAuthenticatedStoreAsync(true));

      // Reset app state
      if (credentials.clearUserData === undefined || credentials.clearUserData === true) {
        await dispatch(resetAppStateAsync({ clearAll: true }));
      }
    }

    dispatch(setIsLoadingCountState(-1));
    dispatch(setIsAuthenticatingState(false));
    return token;
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setIsAuthenticatingState(false));
  }
});

export const fetchUserInfoAsync = createAsyncThunk<{}, string>('auth/fetchUserInfoAsync', async (username, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching user info for user '${username}'`, '[auth] fetchUserInfoAsync');
  try {
    dispatch(setIsLoadingCountState(1));

    const responseData = await getUserInfo(username);
    const state = getState() as RootState;
    let token = {
      ...state.auth.token,
    } as IJwt;
    if (responseData.contact_code) {
      token.contactCode = responseData.contact_code;
    }
    await dispatch(setTokenStoreAsync(token));

    dispatch(setIsLoadingCountState(-1));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1));
  }
});

export const pinInAsync = createAsyncThunk<{}, string>('auth/pinInAsync', async (pin, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Pinning tech in for PIN '${pin}'`, '[auth] pinInAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setIsPinningInState(true));

    const tech = await getPinIn(pin);

    if (tech.code) {
      await dispatch(setWorkCategoriesSyncStoreAsync(undefined));
      await dispatch(setTechnicianStoreAsync(tech));
    }

    dispatch(setIsLoadingCountState(-1));
    dispatch(setIsPinningInState(false));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setIsPinningInState(false));
  }
});

export const pinOutAsync = createAsyncThunk<{}, void>('auth/pinOutAsync', async (nil, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Pinning tech out`, '[auth] pinOutAsync');
  try {
    await dispatch(setTechnicianStoreAsync({} as ITechnician));
    await dispatch(setLabourClockingStoreAsync(undefined));
    await dispatch(setAttendanceClockingStoreAsync(undefined));
    await dispatch(setFiltersOrSortsStoreAsync(0));
    await dispatch(setSelectedBranchCodeStoreAsync(''));
    await dispatch(setTechCodeCheckboxStoreAsync(false));
    await dispatch(setTodaysBookingsCheckboxStoreAsync(false));
    await dispatch(setOrderByDateDescendingStoreAsync(false));
    await dispatch(setGpsServicesUserEnabledStoreAsync(true));
    await dispatch(setSelectedVehicleAttachmentsStoreAsync(getEmptyAttachmentContainer(AttachmentAssetType.WorkshopVehicle)));
    await dispatch(setSelectedTechnicianCommentsState([]));
    await dispatch(setServerClockingDetailStoreAsync(undefined));
    await dispatch(resetAppStateAsync({ clearAll: false }));

    // Clear adhoc clocking data
    await dispatch(setAdhocCommentCacheStoreAsync([]));
    await dispatch(setAdhocAttachmentCacheStoreAsync([]));
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setIsPinningInState(false));
  }
});

export const setSelectedDealerCodeStoreAsync = createAsyncThunk<{}, string>('auth/setSelectedDealerCodeStoreAsync', async (dealerCode, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Set selected dealer code '${dealerCode}' to store`, '[auth] setSelectedDealerCodeStoreAsync');
  try {
    dispatch(setSelectedDealerCodeState(dealerCode));
    await updateConfigString(ConfigName.SelectedDealerCode, dealerCode, false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIsAuthenticatedState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[auth] setIsAuthenticatedState: ${action.payload}`);
      state.isAuthenticated = action.payload;
    },
    setTokenState: (state, action: PayloadAction<IJwt | undefined>) => {
      logConsole(LogType.Info, `[auth] setTokenState: ${action.payload}`);
      state.token = action.payload;
    },
    setTechnicianState: (state, action: PayloadAction<ITechnician | undefined>) => {
      logConsole(LogType.Info, `[auth] setTechnicianState: ${action.payload}`);
      state.technician = action.payload;
    },
    setIsAuthenticatingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[auth] setIsAuthenticatingState: ${action.payload}`);
      state.isAuthenticating = action.payload;
    },
    setIsPinningInState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[auth] setIsPinningInState: ${action.payload}`);
      state.isPinningIn = action.payload;
    },
    setIsRefreshingTokenState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[auth] setIsRefreshingTokenState: ${action.payload}`);
      state.isRefreshingToken = action.payload;
    },
    setSelectedDealerCodeState: (state, action: PayloadAction<string>) => {
      logConsole(LogType.Info, `[auth] setSelectedDealerCodeState: ${action.payload}`);
      state.selectedDealerCode = action.payload;
    },
  },
});

/// REDUCERS

export const { setIsAuthenticatedState, setTokenState, setTechnicianState, setIsAuthenticatingState, setIsPinningInState, setIsRefreshingTokenState, setSelectedDealerCodeState } = authSlice.actions;

/// SELECTORS

export default authSlice.reducer;
